<script lang="ts">
  import type { Poll } from "../../lib/types";
  import Button from "../input/Button.svelte";

  let {
    poll,
    loading = false,
    onVote,
    onClose,
  }: {
    poll: Poll;
    loading?: boolean;
    onVote?: (optionId: string) => void;
    onClose?: () => void;
  } = $props();

  function isSelected(optionId: string): boolean {
    return poll.viewerVoteOptionIds.includes(optionId);
  }

  function formatDateTime(value?: string | null): string {
    if (!value) return "제한 없음";
    return new Date(value).toLocaleString("ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function selectionTypeLabel(): string {
    return poll.selectionType === "multiple_choice" ? "복수 선택" : "단일 선택";
  }
</script>

<div class="poll-card rounded-xl p-4">
  <div class="flex items-start justify-between gap-4 mb-4">
    <div>
      <div class="flex items-center gap-2 flex-wrap mb-2">
        <h3 class="text-lg font-semibold poll-card__title">{poll.title}</h3>
        <span class="poll-card__badge {poll.status === 'open' ? 'poll-card__badge--open' : 'poll-card__badge--closed'}">
          {poll.status === "open" ? "진행중" : "마감"}
        </span>
        <span class="poll-card__badge poll-card__badge--type">{selectionTypeLabel()}</span>
      </div>
      {#if poll.description}
        <p class="text-sm poll-card__meta mb-2">{poll.description}</p>
      {/if}
      <div class="text-xs poll-card__meta flex flex-wrap gap-3">
        <span>작성자 {poll.createdBy.name}</span>
        <span>마감 {formatDateTime(poll.endsAt)}</span>
        <span>총 {poll.totalVotes}표</span>
      </div>
    </div>
    {#if poll.status === "open" && onClose}
      <Button variant="secondary" size="sm" onclick={onClose}>마감</Button>
    {/if}
  </div>

  <div class="space-y-2">
    {#each poll.options as option (option.id)}
      <button
        type="button"
        class="poll-card__option w-full text-left rounded-lg p-3 transition-colors {isSelected(option.id) ? 'poll-card__option--selected' : ''}"
        disabled={loading || poll.status !== 'open'}
        onclick={() => onVote?.(option.id)}
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="font-medium poll-card__title">{option.title}</p>
            {#if option.description}
              <p class="text-sm poll-card__meta mt-1">{option.description}</p>
            {/if}
          </div>
          <div class="text-right flex-shrink-0">
            <p class="text-sm font-semibold poll-card__title">{option.voteCount}표</p>
            {#if isSelected(option.id)}
              <p class="text-xs poll-card__accent mt-1">내 선택</p>
            {/if}
          </div>
        </div>
      </button>
    {/each}
  </div>
</div>

<style>
  .poll-card {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  }

  .poll-card__title {
    color: var(--text-primary);
  }

  .poll-card__meta {
    color: var(--text-secondary);
  }

  .poll-card__accent {
    color: var(--accent);
  }

  .poll-card__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 9999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
  }

  .poll-card__badge--open {
    background: rgba(16, 185, 129, 0.12);
    color: #34d399;
  }

  .poll-card__badge--closed {
    background: rgba(239, 68, 68, 0.12);
    color: #f87171;
  }

  .poll-card__badge--type {
    background: color-mix(in srgb, var(--accent-dim) 70%, var(--surface-hover));
    color: var(--accent);
  }

  .poll-card__option {
    background: color-mix(in srgb, var(--surface-hover) 92%, transparent);
    border: 1px solid color-mix(in srgb, var(--border) 90%, transparent);
  }

  .poll-card__option:hover:enabled {
    background: color-mix(in srgb, var(--accent-dim) 55%, var(--surface-hover));
    border-color: color-mix(in srgb, var(--accent) 28%, var(--border));
  }

  .poll-card__option--selected {
    background: color-mix(in srgb, var(--accent-dim) 78%, var(--surface-hover));
    border-color: color-mix(in srgb, var(--accent) 40%, var(--border));
  }
</style>
